import { SelectionVisuals } from './SelectionVisuals';
import { MeshRenderer } from '@ikea-aoa/elements-3d';
import { Color } from '@ikea-aoa/elements-3d/lib/interfaces';
import { SceneObject } from '@ikea-aoa/component-view-3d';

export class OverlayAnimation extends SelectionVisuals {
	public props: {
		color: Color;
		duration: number;
		selected: Array<SceneObject>;
		unselected: Array<SceneObject>;
	} = {
		color: { r: 1, g: 1, b: 1, a: 0.5 },
		duration: 350,
		selected: [],
		unselected: [],
	};
	private record = { factor: 0, interval: 0, reverse: false };

	public animateSelection(): void {
		const color = {
			r: this.props.color.r,
			g: this.props.color.g,
			b: this.props.color.b,
			a: 0,
		};
		this.setOverlayColor(color, this.props.selected);
		this.setOverlayColor(this.props.color, this.props.unselected);

		if (this.props.selected.length > 1) {
			this.createAnimation();
		}
	}
	public animateDeselection(): void {
		const color = {
			r: this.props.color.r,
			g: this.props.color.g,
			b: this.props.color.b,
			a: 0,
		};
		this.setOverlayColor(color, this.props.selected);
		this.setOverlayColor(color, this.props.unselected);
		this.clearAnimation();
	}

	private createAnimation(): void {
		this.clearAnimation();

		const { duration: duration, color } = this.props;
		const i = duration / 25;
		const tick = 2 / i;

		this.record.interval = window.setInterval(() => {
			this.record.factor = !this.record.reverse
				? this.record.factor + tick
				: this.record.factor - tick;
			const fromColor = {
				r: this.props.color.r,
				g: this.props.color.g,
				b: this.props.color.b,
				a: 0,
			};

			const newColor = this.factorToColor(this.record.factor, fromColor, color, this.easeOutCubic);

			if (!this.record.reverse && Math.abs(this.record.factor - 1) < 0.05) {
				this.record.reverse = true;
			} else if (this.record.reverse && Math.abs(this.record.factor - 0) < 0.05) {
				this.setOverlayColor(fromColor, this.props.selected);
				this.clearAnimation();
			} else {
				this.setOverlayColor(newColor, this.props.selected);
			}
		}, 25);
	}

	private setOverlayColor(color: Color, objects: Array<SceneObject>): void {
		objects.forEach((object) => {
			if (object && object.node) {
				const render = object.node.getComponent<MeshRenderer>('MeshRenderer');
				if (render) {
					const { state } = render;

					state.enableOverlay = true;
					state.overlayColor = color;
				}
			}
		});
	}

	private clearAnimation(): void {
		clearInterval(this.record.interval);
		this.record.reverse = false;
		this.record.factor = 0;
	}

	private factorToColor(
		factor: number,
		from: Color,
		to: Color,
		easingFunction: (x: number) => number
	): Color {
		const r = this.factorToValue(factor, from.r as number, to.r as number, easingFunction);
		const g = this.factorToValue(factor, from.g as number, to.g as number, easingFunction);
		const b = this.factorToValue(factor, from.b as number, to.b as number, easingFunction);
		const a = this.factorToValue(factor, from.a as number, to.a as number, easingFunction);

		return {
			r,
			g,
			b,
			a,
		} as Color;
	}

	// TODO it will be nice to have multiple easing functions in one place.
	private factorToValue(
		factor: number,
		from: number,
		to: number,
		easingFunction: (x: number) => number
	): number {
		const diff = to - from;
		const easing = easingFunction(factor);
		return from + diff * easing;
	}

	private easeOutCubic(factor: number): number {
		return 1 - Math.pow(1 - factor, 3);
	}
}
